import React, {type ReactElement} from "react";

import {css, generateKeyId} from "/src/utils/utils";

import Image, {ImageObject} from "../image/Image";
import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import ModuleBody from "../layouts/module-body/ModuleBody";

import * as styles from "./partnership-module.module.scss";

export interface PartnershipModuleProps
  extends Omit<Queries.PartnershipModuleFragmentFragment, "__typename"> {
  classes?: string;
}

const NUM_OF_MARQUEE_LOGOS = 6;
const NUM_OF_MARQUEE_GROUPS = 2;

interface PartnershipModuleMarqueeProps {
  logos: Array<ImageObject>;
}

const Marquee = (props: PartnershipModuleMarqueeProps): ReactElement | null => {
  const {logos} = props;
  const now = React.useRef(Date.now());

  if (!logos && (logos as Array<ImageObject>).length) {
    return null;
  }

  return (
    <div className={css(styles.partnershipModuleMarquee)}>
      {Array.from(Array(NUM_OF_MARQUEE_GROUPS)).map((item, index) => (
        <ul
          aria-hidden={index > 0 ? "true" : "false"}
          className={css(styles.partnershipModuleMarquee__group)}
          key={generateKeyId(`marquee-group-${index}-${now.current}`)}
        >
          {logos.map((logo, i) => (
            <li
              className={css(styles.partnershipModuleMarquee__item)}
              // eslint-disable-next-line react/no-array-index-key
              key={generateKeyId(`marquee-group-item-${i}-${now.current}`)}
            >
              <Image objectFit="contain" image={logo} />
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
};

const PartnershipModule = (props: PartnershipModuleProps): ReactElement => {
  const {
    ctaButton,
    title,
    subtitle,
    logoImages,
    marquee,
    forceMarqueeMode,
    theme,
    classes,
  } = props;
  const is4Column = logoImages && logoImages.length === 4;
  const logos = marquee
    ? logoImages?.slice(0, NUM_OF_MARQUEE_LOGOS)
    : logoImages;

  return (
    <section
      className={css(
        styles.partnershipModule,
        theme === "dark" ? styles.partnershipModuleDark : "",
        theme === "grey" ? styles.partnershipModuleGrey : "",
        marquee ? styles.partnershipModuleWithMarquee : "",
        forceMarqueeMode ? styles.partnershipModuleForceMarquee : "",
        classes!
      )}
    >
      <ModuleBody>
        {(title || subtitle) && (
          <div className={styles.partnershipModule__copies}>
            {title && (
              <h2
                className={css("h2", styles.partnershipModule__copies__header)}
              >
                {title}
              </h2>
            )}

            {subtitle && (
              <p className={css("p", styles.partnershipModule__copies__sub)}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        {marquee && (
          <Marquee logos={(logoImages as Array<ImageObject>) || []} />
        )}

        <ul
          className={css(
            styles.partnershipModule__logos,
            is4Column ? styles.partnershipModule__logos4Col : ""
          )}
        >
          {logos?.map((logo, i) => {
            return (
              <li
                key={logo?.title || `partnership-image-${i}`}
                className={styles.partnershipModule__logosItem}
              >
                <Image
                  image={logo as ImageObject}
                  objectFit="contain"
                  classes={styles.partnershipModule__logo}
                />
              </li>
            );
          })}
        </ul>

        {ctaButton && (
          <CTAButton
            kind="secondary"
            classes={styles.partnershipModule__cta}
            darkMode={theme === "dark"}
            cta={ctaButton as CTA}
          />
        )}
      </ModuleBody>
    </section>
  );
};

export default PartnershipModule;
